<script setup lang="ts">
import { USER_V2_FIELD_CLASS as fieldClass } from './types';
import { useBiometricAuth } from '~/composables/useBiometricAuth';

// Tela "Login e segurança" do Minha Conta V2 (ENG-362).
// Figma: mobile 19771:42070 / desktop 19771:52367 — seção "Seus dados" com
// campos-cartão (UserV2FieldCard). As ações reusam os fluxos reais da página
// antiga (pages/user/security.vue):
// - Senha: useAuthStore().changePassword (components/user/account/security/Password.vue)
// - 2FA: PATCH /bff/users/self-two-factor (TwoFactorAuth.vue)
// - Contas sociais: bff/users/account-social (SocialAccounts.vue)
// Como na página antiga, mutações sensíveis passam pelo containment
// ('update_data') com kyc_id/password_check quando presentes.
const { $containment, $socialAuth } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { changePassword } = useAuthStore();
const { ApiService } = useApiService();
const {
  userId,
  userEmail,
  isTwoFactorEnabled,
  userTwoFactorType,
  fetchUserProfile,
} = useUserData();

const { isTwoFactorAuthEnabled } = useAuth();

// Biometria (nativo): mantida em paridade com Password.vue — ao trocar a senha
// a credencial biométrica é re-enrolada ou desativada.
const {
  isAvailableEnvironment: isNativeAppEnv,
  checkAvailability,
  enroll,
  disable,
  markCredentialOwner,
  clearCredentialOwner,
} = useBiometricAuth();

async function updateBiometricCredential(newPassword: string): Promise<void> {
  if (!isNativeAppEnv()) return;

  const availability = await checkAvailability();

  if (!availability.hasCredential) return;

  const result = await enroll(userEmail.value, newPassword);

  if (result.success) {
    markCredentialOwner(String(userId.value));

    openToaster({
      message: 'Biometria atualizada com a nova senha.',
      type: 'success',
    });

    return;
  }

  await disable();
  clearCredentialOwner();

  openToaster({
    message:
      'Biometria desativada pois a senha foi alterada. Reative em Login e Segurança.',
    type: 'warning',
  });
}

const {
  isSocialAuthEnabled,
  isGoogleButtonActive,
  isFacebookButtonActive,
  isTwitchButtonActive,
  isSteamButtonActive,
} = useFeatureFlagsNew();

type Panel = '' | 'password' | 'two-factor' | 'social';

const openPanel = ref<Panel>('');

function togglePanel(panel: Panel) {
  openPanel.value = openPanel.value === panel ? '' : panel;
}

// --- Senha -----------------------------------------------------------------
const isPasswordLoading = ref(false);
const passwordFields = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
});

async function onPasswordSubmit() {
  if (!userId.value) {
    return;
  }

  if (!passwordFields.value.currentPassword.length) {
    openToaster({
      message: t('auth.change_password.error.current_password'),
      type: 'error',
    });

    return;
  }

  if (!passwordFields.value.newPassword) {
    openToaster({
      message: t('auth.change_password.error.new_password'),
      type: 'error',
    });

    return;
  }

  if (
    passwordFields.value.newPassword !== passwordFields.value.confirmPassword
  ) {
    openToaster({
      message: t('auth.change_password.error.password_confirmation'),
      type: 'error',
    });

    return;
  }

  if (
    passwordFields.value.currentPassword.length < 6 ||
    passwordFields.value.newPassword.length < 6 ||
    passwordFields.value.confirmPassword.length < 6
  ) {
    openToaster({
      message: t('auth.change_password.error.min_length'),
      type: 'error',
    });

    return;
  }

  await $containment.handle('update_data', () => {
    sendPasswordForm();
  });
}

async function sendPasswordForm() {
  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  const sendData: IChangePassword = {
    current_password: passwordFields.value.currentPassword,
    new_password: passwordFields.value.newPassword,
    new_password_confirmation: passwordFields.value.confirmPassword,
  };

  if ($kyc?.current?.id) {
    sendData.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    sendData.password_check = temporaryPassword.get();
  }

  isPasswordLoading.value = true;

  await changePassword(userId.value, sendData)
    .then(async (data) => {
      if (data.status.toString().startsWith('20')) {
        openToaster({
          message:
            data.message ??
            data.data?.message ??
            t('auth.change_password.form_success'),
          type: 'success',
        });

        await fetchUserProfile({ force: true });

        await updateBiometricCredential(passwordFields.value.newPassword);

        passwordFields.value = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        };
        openPanel.value = '';
      } else {
        const message = data.message
          ? data.message
          : data.errors?.[0]
            ? data.errors[0]
            : t('auth.login.error.reset_password');

        openToaster({
          message,
          type: 'error',
        });
      }
    })
    .catch((err) => {
      const errorMessage =
        err?.data && err.data?.message
          ? err.data.message
          : t('auth.change_password.error.current_password_invalid');

      openToaster({
        message: errorMessage,
        type: 'error',
      });
    })
    .finally(() => {
      isPasswordLoading.value = false;
      temporaryPassword.clear();
    });
}

// --- Autenticação de dois fatores -------------------------------------------
const isTwoFactorLoading = ref(false);
const twoFactorForm = ref({
  enabled: 0,
  type: 'email' as 'email' | 'sms',
});

const twoFactorValue = computed(() => {
  return isTwoFactorEnabled.value ? 'Ativado' : 'Desativado';
});

const twoFactorMethodOptions = [
  { value: 'email', label: t('account.security.two_factor_auth.email') },
  { value: 'sms', label: t('account.security.two_factor_auth.sms') },
];

const currentTwoFactorTypeSelected = computed<string>(() => {
  return (
    twoFactorMethodOptions.find(
      (option) => option.value === twoFactorForm.value.type,
    )?.label || ''
  );
});

watch(
  [isTwoFactorEnabled, userTwoFactorType],
  () => {
    twoFactorForm.value = {
      enabled: isTwoFactorEnabled.value ? 1 : 0,
      type: userTwoFactorType.value || 'email',
    };
  },
  { immediate: true },
);

async function onTwoFactorSubmit() {
  if (!isTwoFactorAuthEnabled.value || !twoFactorForm.value.type) {
    return;
  }

  await $containment.handle('update_data', () => {
    sendTwoFactorForm();
  });
}

async function sendTwoFactorForm() {
  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  isTwoFactorLoading.value = true;

  const sendData: Record<string, unknown> = {
    two_factor_enabled: twoFactorForm.value.enabled,
    two_factor_type: twoFactorForm.value.type,
  };

  if ($kyc?.current?.id) {
    sendData.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    sendData.password_check = temporaryPassword.get();
  }

  await ApiService.patch('/bff/users/self-two-factor', sendData)
    .then(async () => {
      const message = twoFactorForm.value.enabled
        ? t('account.security.two_factor_auth.activated', {
            type: currentTwoFactorTypeSelected.value,
          })
        : t('account.security.two_factor_auth.unactivated');

      openToaster({
        message,
        type: 'success',
      });

      await fetchUserProfile({ force: true });

      openPanel.value = '';
    })
    .catch((error) => {
      openToaster({
        message: error?.data?.message || t('general.error'),
        type: 'error',
      });
    })
    .finally(() => {
      isTwoFactorLoading.value = false;
      temporaryPassword.clear();
    });
}

// --- Contas de redes sociais -------------------------------------------------
interface SocialAccount {
  id?: number;
  social_type: 'facebook' | 'google' | 'twitch' | 'steam';
  name: string;
  logo: string;
  isAvailable: boolean;
  isConnected: boolean;
}

const isSocialLoading = ref(false);
const loadingSocialAccounts = ref(false);

const socialAccountsList = ref<SocialAccount[]>([
  {
    social_type: 'google',
    name: 'Google',
    logo: 'social/google-color',
    isAvailable: isGoogleButtonActive.value,
    isConnected: false,
  },
  {
    social_type: 'twitch',
    name: 'Twitch',
    logo: 'social/twitch-color',
    isAvailable: isTwitchButtonActive.value,
    isConnected: false,
  },
  {
    social_type: 'facebook',
    name: 'Facebook',
    logo: 'social/facebook-color',
    isAvailable: isFacebookButtonActive.value,
    isConnected: false,
  },
  {
    social_type: 'steam',
    name: 'Steam',
    logo: 'social/steam-color',
    isAvailable: isSteamButtonActive.value,
    isConnected: false,
  },
]);

const socialAccountsConnected = computed(() => {
  return socialAccountsList.value.filter((account) => account.isConnected);
});

const socialValue = computed(() => {
  if (!isSocialAuthEnabled.value || loadingSocialAccounts.value) {
    return 'Você não tem nenhuma conta conectada';
  }

  if (!socialAccountsConnected.value.length) {
    return 'Você não tem nenhuma conta conectada';
  }

  return `${t('account.security.social_accounts.connected')}: ${socialAccountsConnected.value
    .map((account) => account.name)
    .join(' | ')}`;
});

async function fetchSocialAccounts() {
  loadingSocialAccounts.value = true;

  const { data } = await ApiService.get('bff/users/account-social');

  if (data?.length) {
    for (const connected of data) {
      const account = socialAccountsList.value.find(
        (item) => item.social_type === connected?.social_type,
      );

      if (account) {
        account.isConnected = true;
        account.id = connected.id;
      }
    }
  }

  loadingSocialAccounts.value = false;
}

async function onConnectSocialClick(account: SocialAccount) {
  if (!account.social_type) {
    return;
  }

  const hasUserConfirmed = confirm(
    t('account.security.social_accounts.connect_confirmation'),
  );

  if (!hasUserConfirmed) {
    return;
  }

  isSocialLoading.value = true;

  await ApiService.post(
    `bff/users/account-social/connect/${account.social_type}`,
    {},
  )
    .then(async ({ data }) => {
      await $socialAuth.provider(account.social_type).connect({
        connectParams: {
          token: data.token,
          ts: data.ts,
          hash: data.hash,
          salt: data.salt,
        },
      });
    })
    .catch(() => {
      openToaster({
        message: t('general.error'),
        type: 'error',
      });
    })
    .finally(() => {
      isSocialLoading.value = false;
    });
}

async function onDisconnectSocialClick(account: SocialAccount) {
  if (!account.id) {
    return;
  }

  const hasUserConfirmed = confirm(
    t('account.security.social_accounts.disconnect_confirmation'),
  );

  if (!hasUserConfirmed) {
    return;
  }

  isSocialLoading.value = true;

  await ApiService.delete(`bff/users/account-social/${account.id}`).then(
    async () => {
      account.isConnected = false;

      await fetchSocialAccounts();
    },
  );

  isSocialLoading.value = false;
}

onMounted(() => {
  // Só busca o perfil se ainda não veio. Chamar fetchUserProfile aqui sempre
  // alterna userDataStore.isLoading (true→false), o que redispara o watcher de
  // "pronto" da loja (useSmarticoUserReady.isReady) e faz a loja ATRÁS do modal
  // recarregar os itens (StoreV2.watch(isReady) → loadshopItems force). Com o
  // perfil já carregado, pulamos e evitamos esse refetch em cascata.
  if (!userEmail.value) {
    fetchUserProfile({ force: true });
  }

  if (isSocialAuthEnabled.value) {
    fetchSocialAccounts();
  }
});
</script>

<template>
  <div
    class="border-solid flex flex-col gap-[10px] rounded-[16px] border border-v2-default/40 p-[16px] md:border-0 md:p-0"
  >
    <p class="text-[14px] leading-[20px] text-white md:hidden">Seus dados</p>

    <!-- Senha -->
    <UserV2FieldCard
      icon="edit"
      label="Senha"
      value="**********"
      @click="togglePanel('password')"
    />
    <form
      v-if="openPanel === 'password'"
      class="flex flex-col gap-[12px] rounded-[12px] bg-v2-default/40 p-[16px]"
      @submit.prevent="onPasswordSubmit"
    >
      <div class="flex flex-col gap-[4px]">
        <label
          class="text-[12px] leading-[16px] text-v2-default-700"
          for="security-current-password"
        >
          Senha atual
        </label>
        <input
          id="security-current-password"
          v-model="passwordFields.currentPassword"
          autocomplete="current-password"
          :class="fieldClass"
          type="password"
        />
      </div>
      <div class="flex flex-col gap-[4px]">
        <label
          class="text-[12px] leading-[16px] text-v2-default-700"
          for="security-new-password"
        >
          Nova senha
        </label>
        <input
          id="security-new-password"
          v-model="passwordFields.newPassword"
          autocomplete="new-password"
          :class="fieldClass"
          type="password"
        />
      </div>
      <div class="flex flex-col gap-[4px]">
        <label
          class="text-[12px] leading-[16px] text-v2-default-700"
          for="security-confirm-password"
        >
          Confirmar nova senha
        </label>
        <input
          id="security-confirm-password"
          v-model="passwordFields.confirmPassword"
          autocomplete="new-password"
          :class="fieldClass"
          type="password"
        />
      </div>
      <button
        class="flex h-[40px] w-full items-center justify-center rounded-[12px] bg-v2-primary text-[14px] text-white disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="isPasswordLoading"
        type="submit"
      >
        Alterar senha
      </button>
    </form>

    <!-- Autenticação de dois fatores ('Em breve' quando desabilitada) -->
    <UserV2FieldCard
      :icon="isTwoFactorAuthEnabled ? 'lock' : undefined"
      label="Autenticação de dois fatores"
      :value="isTwoFactorAuthEnabled ? twoFactorValue : t('general.soon')"
      @click="isTwoFactorAuthEnabled && togglePanel('two-factor')"
    />
    <div
      v-if="openPanel === 'two-factor' && isTwoFactorAuthEnabled"
      class="flex flex-col gap-[12px] rounded-[12px] bg-v2-default/40 p-[16px]"
    >
      <div class="flex flex-col gap-[8px]">
        <p class="text-[12px] leading-[16px] text-v2-default-700">
          Ativar autenticação de dois fatores:
        </p>
        <div class="flex gap-[8px]">
          <button
            class="h-[32px] rounded-[12px] px-[16px] text-[13px]"
            :class="
              twoFactorForm.enabled === 0
                ? 'bg-v2-primary text-white'
                : 'bg-v2-default/40 text-v2-foreground'
            "
            type="button"
            @click="twoFactorForm.enabled = 0"
          >
            Não
          </button>
          <button
            class="h-[32px] rounded-[12px] px-[16px] text-[13px]"
            :class="
              twoFactorForm.enabled === 1
                ? 'bg-v2-primary text-white'
                : 'bg-v2-default/40 text-v2-foreground'
            "
            type="button"
            @click="twoFactorForm.enabled = 1"
          >
            Sim
          </button>
        </div>
      </div>

      <div
        v-if="twoFactorForm.enabled === 1"
        class="flex flex-col gap-[4px]"
      >
        <label
          class="text-[12px] leading-[16px] text-v2-default-700"
          for="security-two-factor-method"
        >
          Método de verificação
        </label>
        <select
          id="security-two-factor-method"
          v-model="twoFactorForm.type"
          :class="fieldClass"
        >
          <option
            v-for="option in twoFactorMethodOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <button
        class="flex h-[40px] w-full items-center justify-center rounded-[12px] bg-v2-primary text-[14px] text-white disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="isTwoFactorLoading"
        type="button"
        @click="onTwoFactorSubmit"
      >
        Salvar
      </button>
    </div>

    <!-- Contas de redes sociais (seção some quando desabilitada) -->
    <UserV2FieldCard
      v-if="isSocialAuthEnabled"
      icon="edit"
      label="Contas Redes Sociais"
      :value="socialValue"
      @click="togglePanel('social')"
    />
    <div
      v-if="openPanel === 'social' && isSocialAuthEnabled"
      class="flex flex-col gap-[12px] rounded-[12px] bg-v2-default/40 p-[16px]"
    >
      <p class="text-[12px] leading-[16px] text-v2-default-900">
        {{ t('account.security.social_accounts.desc', { appName: appName() }) }}
      </p>

      <div
        v-for="account in socialAccountsList"
        :key="account.social_type"
        class="border-solid flex items-center justify-between border-b border-white/10 py-[10px] last:border-0"
      >
        <div class="flex items-center gap-[10px]">
          <NuxtIcon
            aria-hidden="true"
            class="text-[24px]"
            filled
            :name="account.logo"
          />
          <p class="text-[14px] font-medium text-white">
            {{ account.name }}
          </p>
        </div>

        <button
          v-if="!account.isAvailable"
          class="h-[32px] rounded-[12px] bg-v2-default/40 px-[12px] text-[12px] text-v2-foreground/60"
          disabled
          type="button"
        >
          {{ t('general.soon') }}
        </button>
        <button
          v-else-if="!account.isConnected"
          class="h-[32px] rounded-[12px] bg-v2-primary px-[12px] text-[12px] text-white disabled:opacity-50"
          :disabled="isSocialLoading"
          type="button"
          @click="onConnectSocialClick(account)"
        >
          {{ t('account.security.social_accounts.connect') }}
        </button>
        <button
          v-else
          class="h-[32px] rounded-[12px] bg-v2-danger/20 px-[12px] text-[12px] text-v2-danger disabled:opacity-50"
          :disabled="isSocialLoading"
          type="button"
          @click="onDisconnectSocialClick(account)"
        >
          {{ t('account.security.social_accounts.disconnect') }}
        </button>
      </div>
    </div>

    <!-- Biometria: reaproveita o fluxo real (self-gating por ambiente
           nativo; fora do app mostra indisponível), igual à página antiga -->
    <UserAccountSecurityBiometric />
  </div>
</template>
